import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  usePushLog,
  useReduxSelector,
  useScrollTopOnChangePage,
} from 'services/hooks';
import { checkNtuHighestLevel, objectEqual, recordValue } from 'services/utils';

import {
  EducationModal,
  EducationPage,
  IEducationState,
} from './education.typing';
import { INIT_EDUCATION_STATE } from './education.constants';

import { callFunctionAction } from 'services/api';
import { PlayerActions } from 'services/redux';

export const useEducation = () => {
  const [state, setState] = useState(INIT_EDUCATION_STATE);

  const educationLevels = useReduxSelector(
    (redux) => redux.app.educationLevels,
    objectEqual
  );
  const passTestRating = useReduxSelector(
    (redux) => redux.app.PASS_TEST_RATING
  );

  const version = useReduxSelector((redux) => redux.app.version);

  const pushLog = usePushLog();
  const dispatch = useDispatch();

  useScrollTopOnChangePage(state.page);

  const updateState = useCallback(
    (partialState: Partial<IEducationState>) =>
      setState((prev) => ({ ...prev, ...partialState })),
    []
  );

  const onAnswerQuestion = useCallback(
    (percent: number) =>
      // Increase rating with each question answer
      setState((prev) => ({ ...prev, rating: prev.rating + percent })),
    []
  );

  const clearState = useCallback(() => setState(INIT_EDUCATION_STATE), []);

  const setSector = useCallback(
    (sector: string) => updateState({ sector }),
    [updateState]
  );

  const setModal = useCallback(
    (modal?: EducationModal) => updateState({ modal }),
    [updateState]
  );

  const setPage = useCallback(
    (page: EducationPage) => updateState({ page }),
    [updateState]
  );

  const closeModal = useCallback(() => setModal(), [setModal]);

  const setLevelPage = useCallback(
    () => setPage(EducationPage.Level),
    [setPage]
  );

  const setLevel = useCallback(
    (level: string) => updateState({ level }),
    [updateState]
  );

  const onPass = useCallback(
    async (level: string, sector: string | undefined, rating: number) => {
      if (!version) {
        return;
      }

      updateState({ isLoading: true });

      try {
        const result = await callFunctionAction('@EDUCATION/COMPLETE', {
          education: { level, sector, rating },
        });

        dispatch(PlayerActions.merge(result.mergePlayer));

        updateState({
          learnedAbilities: result.learntSkills,
          isLoading: false,
        });

        pushLog({
          type: 'education',
          action: 'end',
          params: {
            result: 'complete',
            level,
            sector,
          },
        });
      } catch (error) {
        console.error(error);
        updateState({ isLoading: false });
      }
    },
    [version, dispatch, updateState, pushLog]
  );

  const onSetSector = useCallback(
    (newSector: string) => {
      if (!state.level) {
        return;
      }

      const isUniversityLevel = recordValue(
        educationLevels,
        state.level
      )?.isUniversity;

      setModal(
        isUniversityLevel
          ? EducationModal.SelectUniversitySector
          : EducationModal.StartTest
      );

      setSector(newSector);
    },
    [educationLevels, setModal, setSector, state.level]
  );

  const onFailTest = useCallback(
    (level: string, sector?: string) => {
      setModal(EducationModal.NotPass);

      pushLog({
        type: 'education',
        action: 'end',
        params: { result: 'fail', level, sector },
      });
    },
    [pushLog, setModal]
  );

  const onEndTest = useCallback(async () => {
    if (!state.level || state.isLoading) {
      return;
    }

    let passRating = passTestRating;

    if (
      checkNtuHighestLevel({ version, educationLevels, level: state.level })
    ) {
      passRating = 100;
    }

    if (state.rating >= passRating) {
      await onPass(state.level, state.sector, state.rating);

      setModal(EducationModal.Pass);

      return;
    }

    onFailTest(state.level, state.sector);
  }, [
    state.isLoading,
    state.rating,
    state.level,
    state.sector,
    passTestRating,
    version,
    educationLevels,
    onFailTest,
    onPass,
    setModal,
  ]);

  const onCloseFail = useCallback(() => {
    clearState();
  }, [clearState]);

  const onClosePass = useCallback(() => {
    if (!state.isLoading) {
      clearState();
    }
  }, [clearState, state.isLoading]);

  return {
    ...state,

    setModal,
    closeModal,
    setPage,
    setLevelPage,
    onSetSector,
    onAnswerQuestion,
    setLevel,
    onEndTest,
    updateState,
    clearState,
    onCloseFail,
    onClosePass,
  };
};
